import { clamp, sqrt } from "./math";
import type { SimTuning, SurfaceType, TrackData } from "./types";

/**
 * A track resampled from its control-point spline into evenly spaced points. Built once per track;
 * the client also generates its road mesh from these arrays, so what you see is what you collide with.
 */
export interface TrackModel {
  id: string;
  count: number;
  /** Total centre-line length, m. */
  length: number;
  x: Float64Array;
  y: Float64Array;
  z: Float64Array;
  /** Distance along the track at each point, m. */
  dist: Float64Array;
  /** Unit tangent in the ground plane. */
  tx: Float64Array;
  tz: Float64Array;
  /** Wall-to-wall width, m. */
  width: Float64Array;
  bank: Float64Array;
  /** Index into `surfaceNames`. */
  surface: Uint8Array;
  surfaceNames: SurfaceType[];
  startDistance: number;
  finishDistance: number;
  tuning: SimTuning;
}

export interface TrackSample {
  /** Segment index (point i to i + 1). */
  index: number;
  /** Distance along the track, m. */
  distance: number;
  /** Signed offset from the centre-line, m. Positive = left of travel direction. */
  lateral: number;
  /** Interpolated unit tangent. */
  tx: number;
  tz: number;
  /** Centre-line point. */
  x: number;
  y: number;
  z: number;
  /** Half of the wall-to-wall width, m. */
  halfWidth: number;
  bank: number;
  /** Grip multiplier for the surface here. */
  grip: number;
}

export function createTrackSample(): TrackSample {
  return {
    index: 0,
    distance: 0,
    lateral: 0,
    tx: 0,
    tz: 1,
    x: 0,
    y: 0,
    z: 0,
    halfWidth: 1,
    bank: 0,
    grip: 1,
  };
}

const SUBSTEPS = 24;

/** Uniform Catmull-Rom. */
function catmull(p0: number, p1: number, p2: number, p3: number, t: number): number {
  const t2 = t * t;
  const t3 = t2 * t;
  return (
    0.5 *
    (2 * p1 +
      (p2 - p0) * t +
      (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 +
      (3 * p1 - p0 - 3 * p2 + p3) * t3)
  );
}

export function buildTrack(data: TrackData, tuning: SimTuning): TrackModel {
  const cps = data.controlPoints;
  const n = cps.length;
  if (n < 2) throw new Error(`track ${data.id}: needs at least two control points`);

  // Phantom end points keep the spline heading straight through the first and last points.
  const at = (i: number) => {
    const first = cps[0]!;
    const last = cps[n - 1]!;
    if (i < 0) {
      const p1 = cps[1]!;
      return { x: 2 * first.x - p1.x, y: 2 * first.y - p1.y, z: 2 * first.z - p1.z };
    }
    if (i >= n) {
      const p1 = cps[n - 2]!;
      return { x: 2 * last.x - p1.x, y: 2 * last.y - p1.y, z: 2 * last.z - p1.z };
    }
    return cps[i]!;
  };

  const surfaceNames: SurfaceType[] = [];
  const surfaceOfPoint = new Array<number>(n).fill(0);
  const entries = [...data.surfaces].sort((a, b) => a.fromPoint - b.fromPoint);
  for (const entry of entries) {
    let idx = surfaceNames.indexOf(entry.surface);
    if (idx < 0) {
      idx = surfaceNames.length;
      surfaceNames.push(entry.surface);
    }
    for (let i = entry.fromPoint; i < n; i++) surfaceOfPoint[i] = idx;
  }
  if (surfaceNames.length === 0) surfaceNames.push("dry");

  // Dense polyline along the spline.
  const px: number[] = [];
  const py: number[] = [];
  const pz: number[] = [];
  const pw: number[] = [];
  const pb: number[] = [];
  const ps: number[] = [];
  for (let s = 0; s < n - 1; s++) {
    const p0 = at(s - 1);
    const p1 = at(s);
    const p2 = at(s + 1);
    const p3 = at(s + 2);
    const c1 = cps[s]!;
    const c2 = cps[s + 1]!;
    for (let k = 0; k < SUBSTEPS; k++) {
      const t = k / SUBSTEPS;
      px.push(catmull(p0.x, p1.x, p2.x, p3.x, t));
      py.push(catmull(p0.y, p1.y, p2.y, p3.y, t));
      pz.push(catmull(p0.z, p1.z, p2.z, p3.z, t));
      pw.push(c1.width + (c2.width - c1.width) * t);
      pb.push(c1.bank + (c2.bank - c1.bank) * t);
      ps.push(surfaceOfPoint[s]!);
    }
  }
  const lastCp = cps[n - 1]!;
  px.push(lastCp.x);
  py.push(lastCp.y);
  pz.push(lastCp.z);
  pw.push(lastCp.width);
  pb.push(lastCp.bank);
  ps.push(surfaceOfPoint[n - 1]!);

  const m = px.length;
  const cum = new Float64Array(m);
  for (let i = 1; i < m; i++) {
    const dx = px[i]! - px[i - 1]!;
    const dz = pz[i]! - pz[i - 1]!;
    const dy = py[i]! - py[i - 1]!;
    cum[i] = cum[i - 1]! + sqrt(dx * dx + dy * dy + dz * dz);
  }
  const length = cum[m - 1]!;

  // Resample at even spacing (the final point sits exactly at the end).
  const ds = tuning.sampleSpacing;
  let count = Math.floor(length / ds) + 1;
  const tail = length - (count - 1) * ds;
  if (tail > 1e-6) count += 1;

  const x = new Float64Array(count);
  const y = new Float64Array(count);
  const z = new Float64Array(count);
  const dist = new Float64Array(count);
  const width = new Float64Array(count);
  const bank = new Float64Array(count);
  const surface = new Uint8Array(count);

  let j = 0;
  for (let i = 0; i < count; i++) {
    const d = i === count - 1 ? length : i * ds;
    while (j < m - 2 && cum[j + 1]! < d) j++;
    const span = cum[j + 1]! - cum[j]!;
    const u = span > 0 ? clamp((d - cum[j]!) / span, 0, 1) : 0;
    x[i] = px[j]! + (px[j + 1]! - px[j]!) * u;
    y[i] = py[j]! + (py[j + 1]! - py[j]!) * u;
    z[i] = pz[j]! + (pz[j + 1]! - pz[j]!) * u;
    width[i] = pw[j]! + (pw[j + 1]! - pw[j]!) * u;
    bank[i] = pb[j]! + (pb[j + 1]! - pb[j]!) * u;
    surface[i] = ps[j]!;
    dist[i] = d;
  }

  // Tangents by central differences, in the ground plane.
  const tx = new Float64Array(count);
  const tz = new Float64Array(count);
  for (let i = 0; i < count; i++) {
    const a = i === 0 ? 0 : i - 1;
    const b = i === count - 1 ? count - 1 : i + 1;
    const dx = x[b]! - x[a]!;
    const dz = z[b]! - z[a]!;
    const len = sqrt(dx * dx + dz * dz);
    tx[i] = dx / len;
    tz[i] = dz / len;
  }

  return {
    id: data.id,
    count,
    length,
    x,
    y,
    z,
    dist,
    tx,
    tz,
    width,
    bank,
    surface,
    surfaceNames,
    startDistance: data.startDistance,
    finishDistance: length - data.finishFromEnd,
    tuning,
  };
}

/**
 * Nearest point on the track to (px, pz), searching near `hint` (the previous result's segment
 * index). Pass -1 for a full search. Writes into `out`.
 */
export function sampleTrack(
  track: TrackModel,
  px: number,
  pz: number,
  hint: number,
  out: TrackSample,
): TrackSample {
  const last = track.count - 2;
  let lo = 0;
  let hi = last;
  if (hint >= 0) {
    const w = track.tuning.searchWindow;
    lo = Math.max(0, hint - w);
    hi = Math.min(last, hint + w);
  }

  const { x, z } = track;
  let best = Infinity;
  let bestI = lo;
  let bestU = 0;
  for (let i = lo; i <= hi; i++) {
    const ax = x[i]!;
    const az = z[i]!;
    const dx = x[i + 1]! - ax;
    const dz = z[i + 1]! - az;
    const len2 = dx * dx + dz * dz;
    const u = clamp(((px - ax) * dx + (pz - az) * dz) / len2, 0, 1);
    const ex = px - (ax + u * dx);
    const ez = pz - (az + u * dz);
    const d2 = ex * ex + ez * ez;
    if (d2 < best) {
      best = d2;
      bestI = i;
      bestU = u;
    }
  }
  return fillSample(track, bestI, bestU, px, pz, out);
}

function fillSample(
  track: TrackModel,
  i: number,
  u: number,
  px: number,
  pz: number,
  out: TrackSample,
): TrackSample {
  const j = i + 1;
  const cx = track.x[i]! + (track.x[j]! - track.x[i]!) * u;
  const cz = track.z[i]! + (track.z[j]! - track.z[i]!) * u;
  let tx = track.tx[i]! + (track.tx[j]! - track.tx[i]!) * u;
  let tz = track.tz[i]! + (track.tz[j]! - track.tz[i]!) * u;
  const tl = sqrt(tx * tx + tz * tz);
  tx /= tl;
  tz /= tl;

  out.index = i;
  out.distance = track.dist[i]! + (track.dist[j]! - track.dist[i]!) * u;
  // Left of travel is (tz, -tx).
  out.lateral = (px - cx) * tz - (pz - cz) * tx;
  out.tx = tx;
  out.tz = tz;
  out.x = cx;
  out.y = track.y[i]! + (track.y[j]! - track.y[i]!) * u;
  out.z = cz;
  out.halfWidth = (track.width[i]! + (track.width[j]! - track.width[i]!) * u) * 0.5;
  out.bank = track.bank[i]! + (track.bank[j]! - track.bank[i]!) * u;
  const surfaceName = track.surfaceNames[track.surface[i]!]!;
  out.grip = track.tuning.surfaces[surfaceName];
  return out;
}

/** Centre-line point at a given distance along the track. Writes into `out`. */
export function trackPointAt(track: TrackModel, distance: number, out: TrackSample): TrackSample {
  const d = clamp(distance, 0, track.length);
  // Binary search for the segment.
  let lo = 0;
  let hi = track.count - 2;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (track.dist[mid]! <= d) lo = mid;
    else hi = mid - 1;
  }
  const span = track.dist[lo + 1]! - track.dist[lo]!;
  const u = span > 0 ? clamp((d - track.dist[lo]!) / span, 0, 1) : 0;
  const cx = track.x[lo]! + (track.x[lo + 1]! - track.x[lo]!) * u;
  const cz = track.z[lo]! + (track.z[lo + 1]! - track.z[lo]!) * u;
  return fillSample(track, lo, u, cx, cz, out);
}
